// ScatterDemogsChart.jsx
import { useState, useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Label,
  Line,
} from "recharts";

// colour palette for groups (match your legend colours)
const GROUP_COLOURS = {
  Age: "#1f77b4",
  Education: "#ff7f0e",
  Gender: "#d62728",
  Migration: "#2ca02c",
  //"Vote 2025": "#c7c7c7",
};

// matcher because named the columns stupidly
function getGroupKeys(polLevel) {
  const forward =
    polLevel === "past"
      ? {
          D66: "past_vote_D66",
          CDA: "past_vote_CDA",
          GLpvdA: "past_vote_GLpvdA",
          OtherCentre: "past_vote_OtherCentre",
          OtherLeft: "past_vote_OtherLeft",
          OtherRight: "past_vote_OtherRight",
          PVV: "past_vote_PVV",
          VVD: "past_vote_VVD",
          DidNotVote: "past_vote_turnout",
        }
      : (() => {
          const level = polLevel === "LE" ? "LE" : "GE";
          return {
            D66: `D66_${level}_p`,
            PVV: `PVV_${level}_p`,
            VVD: `VVD_${level}_p`,
            GLpvdA: `GLpvdA_${level}_p`,
            CDA: `CDA_${level}_p`,
            OtherRight: `OtherRight_${level}_p`,
            OtherLeft: `OtherLeft_${level}_p`,
            OtherCentre: `OtherCentre_${level}_p`,
            DidNotVote: `turnout_${level}_p`,
          };
        })();

  // invert it: longKey -> shortName
  return Object.fromEntries(
    Object.entries(forward).map(([shortName, longKey]) => [longKey, shortName])
  );
}

// helper to map type_code → group (adjust as needed)
function getGroup(type_code) {
  if (["Age"].includes(type_code)) return "Age";
  if (["Education"].includes(type_code)) return "Education";
  if (["Gender"].includes(type_code)) return "Gender";
  if (["Migration"].includes(type_code)) return "Migration";
  if (["Vote 2025"].includes(type_code)) return "Vote 2025";
  return null;
}

export default function ScatterDemogsChart({
  scatterDemogsRows,
  selectedParty,
  polLevel,
}) {
  const [hovered, setHovered] = useState(null);

  // Make everything safe even if props haven't arrived yet
  const safeRows = scatterDemogsRows ?? [];
  const party_name_lookup = useMemo(() => getGroupKeys(polLevel), [polLevel]);
  const shortParty = party_name_lookup[selectedParty]; // may be undefined if selectedParty not set
  const level = polLevel === "LE" ? "LE" : "GE";



  // build data array for current party (empty until we have what we need)
  const data = useMemo(() => {
    if (!safeRows.length || !selectedParty || !shortParty) return [];

    return safeRows
      .filter((row) => row.type !== "Vote 2025")
      .filter((row) =>
        polLevel === "past" ? row.election === "past" : row.election === level
      )
      .map((row) => {
        const base = Number(row[`${shortParty}_2025`]) || 0;
        const pred = Number(row[`${shortParty}_vi`]) || 0;

        return {
          x: base * 100,
          y: pred * 100,
          name: row.type_name,
          group: getGroup(row.type),
          changelabel: (pred - base) * 100,
          size: Math.abs(pred - base),
        };
      });
  }, [safeRows, selectedParty, shortParty, polLevel, level]);
  
  const sharedDomain = useMemo(() => {
  if (!data.length) return [0, 1];

  const min = Math.min(...data.map(d => Math.min(d.x, d.y)));
  const max = Math.max(...data.map(d => Math.max(d.x, d.y)));

  const minPad = min * 0.9;
  const maxPad = max * 1.1;

  return [minPad, maxPad];
}, [data]);

  // x=y line data (match your padded domains)
const diagData = useMemo(() => {
  const [minPad, maxPad] = sharedDomain;
  return [
    { x: minPad, y: minPad },
    { x: maxPad, y: maxPad },
  ];
}, [sharedDomain]);

  // Now it's safe to early-return (hooks already called)
  if (!selectedParty || !safeRows.length) return null;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <ResponsiveContainer width="100%" height={500}>
        <ScatterChart
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          onMouseLeave={() => setHovered(null)}
        >
          <CartesianGrid />

          <XAxis
            type="number"
            dataKey="x"
            name="2025 Vote Share"
            unit="%"
  domain={sharedDomain}
            tickFormatter={(val) => val.toFixed(0)}
          >
            <Label value="2025 Vote Share" position="insideBottom" offset={-5} />
          </XAxis>

          <YAxis
            type="number"
            dataKey="y"
            name="Predicted Vote Share"
            unit="%"
  domain={sharedDomain}
            tickFormatter={(val) => val.toFixed(0)}
          >
            <Label
              value="Predicted Vote Share"
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: "middle" }}
            />
          </YAxis>

          <ZAxis type="number" dataKey="size" range={[30, 100]} name="Change" />

          {/* x = y reference line */}
          <Line
            type="linear"
            data={diagData}
            dataKey="y"
            dot={false}
            isAnimationActive={false}
            stroke="#666"
            strokeWidth={2}
            strokeDasharray="6 4"
  legendType="none"   // ⬅️ hides from legend
          />

          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingTop: 20 }}
          />

          {Object.keys(GROUP_COLOURS).map((group) => (
            <Scatter
              key={group}
              name={group}
              data={data.filter((d) => d.group === group)}
              fill={GROUP_COLOURS[group]}
              line={false}
              shape="circle"
              dataKey="x"
              onMouseEnter={(e) => setHovered(e)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      {hovered && (
        <div
          style={{
            position: "absolute",
            left: hovered.cx + 20,
            top: hovered.cy,
            background: "white",
            padding: "8px",
            border: "1px solid #ccc",
            pointerEvents: "none",
          }}
        >
          <strong>{hovered.payload.name}</strong> {hovered.payload.group}
          <div>2025 Vote Share: {hovered.payload.x.toFixed(1)}%</div>
          <div>Predicted: {hovered.payload.y.toFixed(1)}%</div>
          <div>
            Change: {hovered.payload.changelabel > 0 ? "+" : ""}
            {hovered.payload.changelabel.toFixed(1)}%
          </div>
        </div>
      )}
    </div>
  );
}
